/**
 * Language and region — the person's own (plan.md step 73; the user,
 * 2026-10-22: "en-us / he-us / en-il … user can change it in user settings";
 * 2026-10-28: "the default language from the system language, and the
 * default country from the location").
 *
 * Two choices, saved as one tag on the ACCOUNT (`profile.locale`) and on the
 * device: the LANGUAGE the words are in (only one the interface has words
 * for) and the REGION that formats dates, numbers and money (any country).
 * "Use my device and location" goes back to the automatic default: the
 * device's language, the region from where the person is (2026-10-28).
 */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { QueryClientContext, useQuery, useQueryClient } from '@tanstack/react-query';
import { COUNTRY_CODES, UI_LANGUAGES, languageOf, normalizeLocale, regionOf } from '@myrave/shared';
import { Button, Card, LoadingScreen, Stack } from '@/ui';
import { api, onSession } from '@/lib/api';
import { dictionaryReady, useT } from '@/i18n';
import { useAuth } from './AuthProvider';
import { useLocale } from './LocaleProvider';
import { intlLocale } from './rich';

/** A language's name in ITSELF — "English", "עברית" — so it can be found by its reader. */
export function languageName(language: string): string {
  try { return new Intl.DisplayNames([language], { type: 'language' }).of(language) ?? language; } catch { return language; }
}

function regionNames(locale: string): { code: string; name: string }[] {
  let names: Intl.DisplayNames | null = null;
  try { names = new Intl.DisplayNames([locale], { type: 'region' }); } catch { /* the code alone */ }
  return COUNTRY_CODES
    .map((code) => ({ code, name: names?.of(code) ?? code }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}

/**
 * Choose a locale — on this device AND, when signed in, on the account, so
 * every device and (later) every email follows it. `null` = where I am.
 */
export function useChooseLocale(): (next: string | null) => void {
  const { setLocale } = useLocale();
  const { user } = useAuth();
  // Read, not required: the switch also renders where no query client is (a bare shell).
  const qc = useContext(QueryClientContext);
  return (next) => {
    const tag = next === null ? null : normalizeLocale(next);
    if (next !== null && !tag) return;
    setLocale(tag);
    if (!user) return;
    choicesHere += 1;
    qc?.setQueryData<{ locale?: string | null } | undefined>(['me', 'profile'], (p) => (p ? { ...p, locale: tag } : p));
    savingLocale = api.patch('/me/profile', { locale: tag }).catch(() => {});
  };
}

/*
 * A choice made on THIS page, and its save to the account — `AccountLocale`'s re-check never
 * undoes it: it waits for the save, and an answer to a question asked before the choice is dropped.
 */
let choicesHere = 0;
let savingLocale: Promise<unknown> = Promise.resolve();

/**
 * The account's choice, applied wherever the person signs in — a new phone
 * opens in the language they chose on the old one. Renders nothing.
 */
export function AccountLocale() {
  const { user } = useAuth();
  const { locale, setLocale, source, chosen } = useLocale();
  const profile = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: () => api.get<{ locale?: string | null }>('/me/profile'),
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
  });
  const saved = normalizeLocale(profile.data?.locale ?? null);
  // What this host remembered when the page opened — a choice made on THIS host, maybe long ago.
  const openedWith = useRef(source === 'chosen' ? locale : null);

  /*
   * The account's language changed on ANOTHER device while this page sat in the background (the
   * user, 2026-10-30: "I changed the language on the account from Hebrew to English on the iPhone,
   * then reopened the app on Android — it didn't fetch the new language until I force-refreshed").
   * The profile was read once per page (five minutes fresh, and the app never refetches on focus),
   * and the phone app resumes the SAME page — so the old language stayed. Asked again whenever the
   * page comes back into view and on every session refresh; the newest choice wins: one made on
   * this page since the question was asked keeps its place.
   */
  const qc = useQueryClient();
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) return;
    let asking = false;
    const ask = async () => {
      if (asking) return;
      asking = true;
      try {
        const choices = choicesHere;
        await savingLocale;
        const fresh = await api.get<{ locale?: string | null }>('/me/profile');
        if (choicesHere !== choices || !fresh) return;
        qc.setQueryData(['me', 'profile'], fresh);
      } catch { /* offline: the next return asks again */ } finally { asking = false; }
    };
    const visible = () => { if (document.visibilityState === 'visible') void ask(); };
    document.addEventListener('visibilitychange', visible);
    const off = onSession((session) => { if (session?.user.id === userId) void ask(); });
    return () => { document.removeEventListener('visibilitychange', visible); off(); };
  }, [userId, qc]);

  // The account's previous answer — so "no choice any more" (set on another device) is heard too.
  const lastSaved = useRef<string | null>(null);
  useEffect(() => {
    const before = lastSaved.current;
    if (profile.isSuccess) lastSaved.current = saved;
    /*
     * Compared with the CHOICE on record, never with what is on screen (the user, 2026-10-29, on an
     * Android phone in Hebrew: "my account is on English and some devices open in Hebrew"). While the
     * device language's table is still loading the screen shows English — `en-IL` — so an account's
     * `en-IL` arriving in that moment looked already applied, was never kept, and the Hebrew table
     * then took the page. Every start on that phone did it again.
     */
    if (saved) {
      if (saved !== chosen) setLocale(saved);
      return;
    }
    // The account's choice was cleared on another device ("Use my device and location"): here too.
    if (profile.isSuccess && before && chosen === before) {
      openedWith.current = null;
      setLocale(null);
      return;
    }
    /*
     * The account has NOTHING chosen, and this host still remembers an old choice (every host is
     * its own origin, so a choice made on `support.` once outlived the account's "no choice" and
     * kept that host in Hebrew on an English computer — the user, 2026-10-28). Signed in, the
     * account decides on every host: the old one goes, and the device's language shows. A choice
     * made on this page since it opened is kept (`useChooseLocale` saves it to the account).
     */
    if (profile.isSuccess && openedWith.current && source === 'chosen' && locale === openedWith.current) {
      openedWith.current = null;
      setLocale(null);
    }
    // Only when the ACCOUNT's value arrives or changes — a choice made here
    // is saved to the account by `useChooseLocale`, never undone by this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, profile.isSuccess]);

  /*
   * Nothing chosen: tell the server which locale the app is SHOWING (step
   * 73.8, `PUT /me/locale-seen`), so its emails, pushes and set reminders
   * speak the language this person is already reading. Once per account and
   * locale per page load; never when they chose one (the account's choice is
   * what the server reads first anyway). Best effort — a failure is retried
   * on the next load.
   */
  const seen = normalizeLocale(locale);
  useEffect(() => {
    // Only the AUTOMATIC language (the device's) is what they read — never a choice (the account
    // has it) and never one visit's `?lang=` (a shared link must not set their emails' language).
    if (!userId || !seen || source !== 'device' || !profile.isSuccess || saved) return;
    const key = `${userId}|${seen}`;
    if (reportedSeen.has(key)) return;
    reportedSeen.add(key);
    void api.put('/me/locale-seen', { locale: seen }).catch(() => { reportedSeen.delete(key); });
  }, [userId, seen, source, profile.isSuccess, saved]);

  /*
   * Never repaint from one direction to the other in view (the user, 2026-10-30, the 0.14.74 walk: "it works but I
   * can see the screen change direction — load the language while the loading screen is on"). Until the account's
   * first answer is in — and whenever its language is not on screen yet (chosen, and its table loaded) — a loading
   * screen covers the page. It carries `data-boot-wait`, so at launch the launch screen itself holds (index.html);
   * on a return to the app it covers the switch. Never a trap: ACCOUNT_LOCALE_WAIT_MS at most each time, and no
   * wait with no signal (the profile cannot come).
   */
  const tr = useT();
  const firstAnswer = Boolean(user) && !profile.isFetched && navigator.onLine !== false;
  const switching = Boolean(saved) && (saved !== chosen || !dictionaryReady(saved!));
  const waiting = firstAnswer || switching;
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!waiting) { setGaveUp(false); return undefined; }
    const t = setTimeout(() => setGaveUp(true), ACCOUNT_LOCALE_WAIT_MS);
    return () => clearTimeout(t);
  }, [waiting]);
  return waiting && !gaveUp ? <LoadingScreen label={tr('One moment…')} /> : null;
}

/** The longest the account's language holds the screen (`AccountLocale`). */
export const ACCOUNT_LOCALE_WAIT_MS = 3000;

/** The locales already reported this page load, by account (`AccountLocale`). */
const reportedSeen = new Set<string>();

export function LanguageSection() {
  const tr = useT();
  const { locale, language, region, source } = useLocale();
  const choose = useChooseLocale();
  const regions = useMemo(() => regionNames(locale), [locale]);
  const now = new Date();
  const sample = [
    new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: 'full' }).format(now),
    new Intl.DateTimeFormat(intlLocale(locale), { timeStyle: 'short' }).format(now),
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(1234.5),
  ].join(' · ');

  return (
    <Card className="account-page-panel">
      <Stack gap={12}>
        <div>
          <div className="app-card-title">{tr('Language and region')}</div>
          <div className="acct-hint">{tr('Words in your language; dates, times and money the way your region writes them.')}</div>
        </div>
        <div className="account-page-fields">
        <div className="account-page-field is-edit">
          <label htmlFor="account-language" className="account-page-field-label">{tr('Language')}</label>
          <select
            id="account-language"
            className="account-page-input"
            value={language}
            onChange={(e) => choose(`${e.target.value}-${region}`)}
          >
            {UI_LANGUAGES.map((l) => <option key={l} value={l} lang={l}>{languageName(l)}</option>)}
          </select>
        </div>
        <div className="account-page-field is-edit">
          <label htmlFor="account-region" className="account-page-field-label">{tr('Region')}</label>
          <select
            id="account-region"
            className="account-page-input"
            value={region}
            onChange={(e) => choose(`${languageOf(locale)}-${e.target.value}`)}
          >
            {regions.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
          </select>
        </div>
        </div>
        <p className="acct-hint" data-testid="locale-sample" dir="auto">{tr('Looks like: {sample}', { sample })}</p>
        {source === 'chosen' ? (
          <span><Button variant="secondary" onClick={() => choose(null)}>{tr('Use my device and location')}</Button></span>
        ) : (
          <p className="acct-hint">{tr('Language from your device, region from where you are ({region}). Change either to choose your own.', { region: regionNames(locale).find((r) => r.code === regionOf(locale))?.name ?? region })}</p>
        )}
      </Stack>
    </Card>
  );
}
